import { useState } from 'react'
import { message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { ENDPOINTS } from '@sportninja/common/api/endpoints'
import { queryKeys } from '@sportninja/common/config/queryKeys'

/** The snake_case fields the divisions grid can commit. */
export type DivisionPatch = Partial<{
  sort_order: number
  name: string
  is_visible_standings: boolean
  lock_roster_date: string | null
}>

export interface UpdateDivisionField {
  updateField: (scheduleId: string, patch: DivisionPatch) => void
  /** Ids with a save in flight, for the pending row treatment. */
  pendingIds: Set<string>
}

/**
 * Commits one inline edit on a division/conference row.
 *
 * `PUT /schedules/:id` accepts a PARTIAL body — the same call the sort-order
 * cell has always used — so each cell sends only what changed and cannot blank
 * its neighbours. (Contrast the team-officials endpoint, which is a full
 * replace; see `toOfficialUpdatePayload`.)
 *
 * Invalidates on SETTLE, not success: a rejected edit refetches the server's
 * value rather than leaving the attempted one on screen.
 */
export const useUpdateDivisionField = (
  rootScheduleId: string
): UpdateDivisionField => {
  'use memo'

  const queryClient = useQueryClient()
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

  const mutation = useMutation({
    mutationFn: async ({
      scheduleId,
      patch,
    }: {
      scheduleId: string
      patch: DivisionPatch
    }) => {
      await req(ENDPOINTS.SCHEDULES.UPDATE(scheduleId), {
        method: 'PUT',
        // `req` hands `body` straight to fetch without serialising, so an
        // object would be sent as the literal string "[object Object]".
        body: JSON.stringify(patch),
      })
    },
    onMutate: ({ scheduleId }) => {
      setPendingIds((current) => new Set(current).add(scheduleId))
    },
    // No success toast: inline cell edits already signal completion through the
    // row's pending dim, and one toast per committed cell would stack up while
    // working down a grid. Errors still surface — a silent failure would leave
    // the attempted value looking saved.
    onError: (error: Error) => {
      message.error(getErrorMessage(error))
    },
    onSettled: (_data, _error, { scheduleId }) => {
      setPendingIds((current) => {
        const next = new Set(current)
        next.delete(scheduleId)
        return next
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.operations.tree(rootScheduleId),
      })
    },
  })

  return {
    updateField: (scheduleId, patch) => mutation.mutate({ scheduleId, patch }),
    pendingIds,
  }
}
